import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box, Flex, Text, Button, Badge, Spinner, Center, HStack, VStack, Select, Input,
  IconButton, Tooltip, useToast, SimpleGrid, Divider, Textarea, Checkbox,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton,
  Drawer, DrawerOverlay, DrawerContent, DrawerHeader, DrawerBody, DrawerCloseButton,
  Table, Thead, Tbody, Tr, Th, Td, Alert, AlertIcon, InputGroup, InputLeftElement,
  Tabs, TabList, TabPanels, Tab, TabPanel, FormControl, FormLabel, Tag, TagLabel,
  TagCloseButton, Wrap, WrapItem,
} from "@chakra-ui/react";
import {
  RefreshCw, Search, FileText, Pencil, CheckCircle, AlertTriangle, Send,
  XCircle, Download, PackageOpen, Truck, Plus, Trash2, ShieldCheck, FlaskConical,
} from "lucide-react";
import {
  getDevolucoesFornecedor, getPerfisDevolucaoFornecedor, lerNotaCompraDevolucao,
  criarDevolucaoFornecedor, getDevolucaoFornecedor, atualizarDevolucaoFornecedor,
  lerAutorizacaoDevolucao,
  removerItemDevolucaoFornecedor, enviarAutorizacaoDevolucaoFornecedor,
  validarDevolucaoFornecedor, emitirDevolucaoFornecedor,
  alterarStatusDevolucaoFornecedor, cancelarDevolucaoFornecedor,
  baixarArquivoDevolucaoFornecedor,
} from "../services/api";
import { formatBRL, formatDate, formatDateTime } from "../utils/formatters";

const ACCENT = "#F68633";

const STATUS = {
  rascunho:   { label: "Rascunho",   cor: "gray" },
  validado:   { label: "Validado",   cor: "blue" },
  autorizado: { label: "Autorizado", cor: "green" },
  enviado:    { label: "Enviado",    cor: "teal" },
  despachado: { label: "Despachado", cor: "purple" },
  confirmado: { label: "Confirmado", cor: "green" },
  recusado:   { label: "Recusado",   cor: "red" },
  cancelado:  { label: "Cancelado",  cor: "gray" },
  rejeitado:  { label: "Rejeitado",  cor: "red" },
};

// O cenário decide CFOP/CSOSN de saída. Mostrado na tela porque é o que o
// fornecedor confere primeiro quando recebe a nota.
const CENARIOS = {
  A: { label: "A · ST na compra",   cor: "orange", ajuda: "Fornecedor é o substituto. ICMS em campo próprio; ST e IPI em Outras Despesas." },
  B: { label: "B · ST já retida",   cor: "purple", ajuda: "CSOSN 500: reproduz a ST retida na origem, sem ICMS próprio." },
  C: { label: "C · sem ST",         cor: "blue",   ajuda: "Operação normal. ICMS em campo próprio." },
};

// Mesma regra do back (filter_var): e-mail torto na lista bloqueia a emissão, então
// a tela marca qual é o errado em vez de deixar o operador procurar.
const emailValido = (email) => /^[^\s@,;]+@[^\s@,;]+\.[^\s@,;]+$/.test(String(email).trim());

const selo = (status) => {
  const s = STATUS[status] || { label: status, cor: "gray" };
  return <Badge colorScheme={s.cor} fontSize="11px" px={2} py={0.5} borderRadius="md">{s.label}</Badge>;
};

export default function DevolucaoFornecedor() {
  const toast = useToast();

  const [lista, setLista] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [perfis, setPerfis] = useState([]);
  // O faturador precisa tratar o campo `ambiente`; enquanto não tratar, o teste fica desligado.
  const [homologacaoDisponivel, setHomologacaoDisponivel] = useState(false);
  const [filtros, setFiltros] = useState({ status: "", perfil: "", busca: "" });

  const [novaAberta, setNovaAberta] = useState(false);
  const [detalheId, setDetalheId] = useState(null);

  const carregar = useCallback(async () => {
    setCarregando(true);
    const res = await getDevolucoesFornecedor({
      status: filtros.status || undefined,
      perfil: filtros.perfil || undefined,
      busca: filtros.busca || undefined,
    });
    setLista(res?.data || []);
    setCarregando(false);
  }, [filtros]);

  useEffect(() => { carregar(); }, [carregar]);

  useEffect(() => {
    (async () => {
      const res = await getPerfisDevolucaoFornecedor();
      setPerfis(res?.perfis || []);
      setHomologacaoDisponivel(!!res?.homologacao_disponivel);
    })();
  }, []);

  return (
    // A sidebar e fixa (64px recolhida) e a navbar do topo tem 56px: sem esse
    // deslocamento o conteudo encosta no menu da esquerda.
    <Box ml={{ base: 0, md: "64px" }} pt="70px" px={{ base: 3, md: 6 }} pb={10} minH="100vh" bg="gray.50">
      <Flex justify="space-between" align="center" mb={1} wrap="wrap" gap={3}>
        <HStack spacing={3}>
          <PackageOpen size={26} color={ACCENT} />
          <Box>
            <Text fontSize="xl" fontWeight="bold">Devolução a Fornecedor</Text>
            <Text fontSize="xs" color="gray.500">
              NF-e de devolução derivada da nota de compra, com as regras de cada fornecedor
            </Text>
          </Box>
        </HStack>
        <HStack>
          <Tooltip label="Atualizar">
            <IconButton size="sm" variant="outline" aria-label="Atualizar"
              icon={<RefreshCw size={16} />} onClick={carregar} isLoading={carregando} />
          </Tooltip>
          <Button size="sm" leftIcon={<Plus size={16} />} bg={ACCENT} color="white"
            _hover={{ bg: "#e07529" }} onClick={() => setNovaAberta(true)}>
            Nova devolução
          </Button>
        </HStack>
      </Flex>

      <Flex gap={3} my={4} wrap="wrap">
        <InputGroup size="sm" maxW="320px">
          <InputLeftElement pointerEvents="none"><Search size={14} /></InputLeftElement>
          <Input placeholder="Autorização, NF de origem ou chave"
            value={filtros.busca}
            onChange={(e) => setFiltros((f) => ({ ...f, busca: e.target.value }))} />
        </InputGroup>
        <Select size="sm" maxW="200px" value={filtros.status}
          onChange={(e) => setFiltros((f) => ({ ...f, status: e.target.value }))}>
          <option value="">Todos os status</option>
          {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </Select>
        <Select size="sm" maxW="200px" value={filtros.perfil}
          onChange={(e) => setFiltros((f) => ({ ...f, perfil: e.target.value }))}>
          <option value="">Todos os fornecedores</option>
          {perfis.map((p) => <option key={p.chave} value={p.chave}>{p.label}</option>)}
        </Select>
      </Flex>

      {carregando ? (
        <Center py={16}><Spinner color={ACCENT} /></Center>
      ) : lista.length === 0 ? (
        <Center py={16}>
          <VStack spacing={2}>
            <PackageOpen size={36} color="#CBD5E0" />
            <Text color="gray.500" fontSize="sm">Nenhuma devolução por aqui ainda.</Text>
          </VStack>
        </Center>
      ) : (
        <Box overflowX="auto" borderWidth="1px" borderRadius="lg">
          <Table size="sm" variant="simple">
            <Thead bg="gray.50">
              <Tr>
                <Th>#</Th><Th>Fornecedor</Th><Th>NF de origem</Th><Th>Autorização</Th>
                <Th isNumeric>Itens</Th><Th isNumeric>Valor</Th><Th>NF-e</Th><Th>Status</Th><Th>Criada</Th>
              </Tr>
            </Thead>
            <Tbody>
              {lista.map((d) => (
                <Tr key={d.id} _hover={{ bg: "gray.50", cursor: "pointer" }}
                  onClick={() => setDetalheId(d.id)}>
                  <Td fontWeight="bold">{d.id}</Td>
                  <Td><Text fontSize="xs" textTransform="uppercase">{d.perfil}</Text></Td>
                  <Td fontSize="xs">{d.nfe_origem_numero || "—"}</Td>
                  <Td fontSize="xs">{d.autorizacao_numero || "—"}</Td>
                  <Td isNumeric>{d.itens_count}</Td>
                  <Td isNumeric fontSize="xs">{formatBRL(d.totais?.vNF || 0)}</Td>
                  <Td fontSize="xs">{d.nfe_numero || "—"}</Td>
                  <Td>{selo(d.status)}</Td>
                  <Td fontSize="xs" color="gray.500">{formatDate(d.created_at)}</Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      )}

      {novaAberta && (
        <ModalNovaDevolucao
          perfis={perfis}
          onFechar={() => setNovaAberta(false)}
          onCriada={(id) => { setNovaAberta(false); carregar(); setDetalheId(id); }}
          toast={toast}
        />
      )}

      {detalheId && (
        <DrawerDetalhe
          id={detalheId}
          perfis={perfis}
          homologacaoDisponivel={homologacaoDisponivel}
          onFechar={() => { setDetalheId(null); carregar(); }}
          toast={toast}
        />
      )}
    </Box>
  );
}

/* ------------------------------------------------------------------------- */
/* Nova devolução — carrega a NF-e de compra, escolhe os itens e cria o rascunho */
/* ------------------------------------------------------------------------- */

function ModalNovaDevolucao({ perfis, onFechar, onCriada, toast }) {
  const [etapa, setEtapa] = useState(1);
  const [perfil, setPerfil] = useState("livre");
  const [carregando, setCarregando] = useState(false);

  const [xml, setXml] = useState("");
  const [chave, setChave] = useState("");
  const [nota, setNota] = useState(null);
  const [selecao, setSelecao] = useState({});

  const [form, setForm] = useState({
    autorizacao_numero: "", autorizacao_data: "", motivo_codigo: "", motivo_texto: "",
    aos_cuidados: "", vendedor: "", observacao: "", transportadora: "",
    data_saida: "", data_prevista_entrega: "",
  });
  const [emails, setEmails] = useState([]);
  const [emailsCopia, setEmailsCopia] = useState([]);

  // PDF da autorização lido já na criação: os campos nascem preenchidos em vez de
  // serem digitados aqui e corrigidos depois no rascunho.
  const [autorizacaoArquivo, setAutorizacaoArquivo] = useState("");
  const [autorizacaoNome, setAutorizacaoNome] = useState("");
  const [lendoPdf, setLendoPdf] = useState(false);

  const perfilAtivo = useMemo(
    () => perfis.find((p) => p.chave === perfil) || null, [perfis, perfil]
  );

  // Trocar de perfil recarrega os e-mails padrão do fornecedor.
  useEffect(() => {
    if (perfilAtivo) {
      setEmails(perfilAtivo.emails || []);
      setEmailsCopia(perfilAtivo.emails_copia || []);
    }
  }, [perfilAtivo]);

  const carregarNota = async (origem, arquivo) => {
    setCarregando(true);
    let res;
    if (origem === "arquivo" && arquivo) {
      const form = new FormData();
      form.append("arquivo", arquivo);
      res = await lerNotaCompraDevolucao(form);
    } else if (origem === "chave") {
      res = await lerNotaCompraDevolucao({ chave });
    } else {
      res = await lerNotaCompraDevolucao({ xml });
    }
    setCarregando(false);

    if (res?.error) {
      toast({ title: "Não foi possível ler a nota", description: res.error, status: "error", duration: 7000 });
      return;
    }

    setNota(res.nota);
    setXml(res.xml);
    if (res.perfil_sugerido && res.perfil_sugerido !== "livre") setPerfil(res.perfil_sugerido);

    // Começa com tudo desmarcado: devolução parcial é o caso comum e incluir na nota
    // um item que não está voltando é pior do que ter de marcar o que está.
    const inicial = {};
    (res.nota.itens || []).forEach((i) => {
      inicial[i.item] = { marcado: false, quantidade: i.quantidade };
    });
    setSelecao(inicial);
    setEtapa(2);
  };

  const itensEscolhidos = useMemo(
    () => Object.entries(selecao)
      .filter(([, v]) => v.marcado && Number(v.quantidade) > 0)
      .map(([item, v]) => ({ item: Number(item), quantidade: Number(v.quantidade) })),
    [selecao]
  );

  const lerPdfAutorizacao = async (arquivo) => {
    setLendoPdf(true);
    const res = await lerAutorizacaoDevolucao(arquivo, perfil);
    setLendoPdf(false);

    if (res?.error) {
      toast({ title: "Não foi possível ler a autorização", description: res.error, status: "error", duration: 7000 });
      return;
    }

    setAutorizacaoArquivo(res.arquivo);
    setAutorizacaoNome(arquivo.name);

    // O que o PDF trouxe preenche o formulário; o que ele não trouxe fica como está.
    const c = res.campos || {};
    setForm((f) => {
      const novo = { ...f };
      [
        "autorizacao_numero", "autorizacao_data", "motivo_codigo", "motivo_texto",
        "aos_cuidados", "vendedor", "transportadora",
      ].forEach((campo) => { if (c[campo]) novo[campo] = c[campo]; });
      return novo;
    });

    toast({
      title: "Autorização lida",
      description: res.origem === "ocr" ? "Campos lidos por OCR — confira antes de criar."
        : res.origem === "indisponivel" ? "Leitura automática indisponível: preencha os campos à mão."
        : "Campos extraídos do PDF — confira antes de criar.",
      status: res.origem === "texto" ? "success" : "warning", duration: 7000,
    });
  };

  const criar = async () => {
    setCarregando(true);
    const res = await criarDevolucaoFornecedor({
      perfil, xml, itens: itensEscolhidos,
      ...form,
      emails, emails_copia: emailsCopia,
      autorizacao_arquivo: autorizacaoArquivo || undefined,
    });
    setCarregando(false);

    if (res?.error) {
      toast({ title: "Erro ao criar a devolução", description: res.error, status: "error", duration: 7000 });
      return;
    }
    toast({ title: `Rascunho #${res.id} criado`, status: "success", duration: 4000 });
    onCriada(res.id);
  };

  return (
    <Modal isOpen onClose={onFechar} size="6xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader fontSize="md">
          Nova devolução a fornecedor
          <Text fontSize="xs" color="gray.500" fontWeight="normal">
            Etapa {etapa} de 3 — {etapa === 1 ? "nota de compra" : etapa === 2 ? "itens a devolver" : "dados da autorização"}
          </Text>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {etapa === 1 && (
            <VStack align="stretch" spacing={4}>
              <FormControl>
                <FormLabel fontSize="sm">Perfil do fornecedor</FormLabel>
                <Select size="sm" value={perfil} onChange={(e) => setPerfil(e.target.value)}>
                  {perfis.map((p) => <option key={p.chave} value={p.chave}>{p.label}</option>)}
                </Select>
                {perfilAtivo && perfilAtivo.chave !== "livre" && (
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    {perfilAtivo.validade_dias
                      ? `Autorização vale ${perfilAtivo.validade_dias} dias. `
                      : ""}
                    {perfilAtivo.transportadoras_proibidas?.length
                      ? `Não aceita ${perfilAtivo.transportadoras_proibidas.join(" / ")}. `
                      : ""}
                    {perfilAtivo.conferencia_espelho ? "Confere a nota campo a campo contra o PDF." : ""}
                  </Text>
                )}
              </FormControl>

              <Divider />

              <Tabs size="sm" colorScheme="orange" variant="soft-rounded">
                <TabList>
                  <Tab>Arquivo XML</Tab><Tab>Colar XML</Tab><Tab>Chave de acesso</Tab>
                </TabList>
                <TabPanels>
                  <TabPanel px={0}>
                    <Input type="file" accept=".xml" size="sm" pt={1}
                      onChange={(e) => e.target.files?.[0] && carregarNota("arquivo", e.target.files[0])} />
                    <Text fontSize="xs" color="gray.500" mt={2}>
                      XML da NF-e de compra que originou a devolução.
                    </Text>
                  </TabPanel>
                  <TabPanel px={0}>
                    <Textarea size="sm" rows={8} placeholder="Cole aqui o XML da NF-e de compra"
                      value={xml} onChange={(e) => setXml(e.target.value)} />
                    <Button mt={2} size="sm" isDisabled={!xml.trim()} isLoading={carregando}
                      onClick={() => carregarNota("xml")}>Ler nota</Button>
                  </TabPanel>
                  <TabPanel px={0}>
                    <Input size="sm" placeholder="44 dígitos" value={chave} maxLength={44}
                      onChange={(e) => setChave(e.target.value.replace(/\D/g, ""))} />
                    <Button mt={2} size="sm" isDisabled={chave.length !== 44} isLoading={carregando}
                      onClick={() => carregarNota("chave")}>Consultar no faturador</Button>
                  </TabPanel>
                </TabPanels>
              </Tabs>
            </VStack>
          )}

          {etapa === 2 && nota && (
            <VStack align="stretch" spacing={3}>
              <Alert status="info" fontSize="xs" borderRadius="md">
                <AlertIcon />
                <Box>
                  <Text fontWeight="bold">
                    NF {nota.numero} — {nota.emitente?.razao_social}
                  </Text>
                  <Text>
                    Emitida em {formatDate(nota.emissao)} · chave {nota.chave}
                  </Text>
                </Box>
              </Alert>

              <Box overflowX="auto" borderWidth="1px" borderRadius="md">
                <Table size="sm">
                  <Thead bg="gray.50">
                    <Tr>
                      <Th w="40px"></Th><Th>Item</Th><Th>NCM</Th><Th>Cenário</Th>
                      <Th isNumeric>Comprado</Th><Th isNumeric>Devolver</Th><Th isNumeric>Unitário</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {nota.itens.map((i) => {
                      const sel = selecao[i.item] || {};
                      const cen = CENARIOS[i.cenario] || {};
                      return (
                        <Tr key={i.item} opacity={sel.marcado ? 1 : 0.45}>
                          <Td>
                            <Checkbox isChecked={!!sel.marcado} colorScheme="orange"
                              onChange={(e) => setSelecao((s) => ({
                                ...s, [i.item]: { ...s[i.item], marcado: e.target.checked },
                              }))} />
                          </Td>
                          <Td>
                            <Text fontSize="xs" fontWeight="bold">{i.codigo}</Text>
                            <Text fontSize="11px" color="gray.500" noOfLines={1}>{i.descricao}</Text>
                          </Td>
                          <Td fontSize="xs">{i.ncm}</Td>
                          <Td>
                            <Tooltip label={cen.ajuda} fontSize="xs">
                              <Badge colorScheme={cen.cor} fontSize="10px">{cen.label}</Badge>
                            </Tooltip>
                          </Td>
                          <Td isNumeric fontSize="xs">{i.quantidade}</Td>
                          <Td isNumeric>
                            <Input size="xs" type="number" w="80px" textAlign="right"
                              min={0} max={i.quantidade} step="any"
                              isDisabled={!sel.marcado}
                              value={sel.quantidade ?? ""}
                              onChange={(e) => setSelecao((s) => ({
                                ...s, [i.item]: { ...s[i.item], quantidade: e.target.value },
                              }))} />
                          </Td>
                          <Td isNumeric fontSize="xs">{formatBRL(i.valor_unitario)}</Td>
                        </Tr>
                      );
                    })}
                  </Tbody>
                </Table>
              </Box>
              <Text fontSize="xs" color="gray.500">
                O tratamento tributário de cada item é derivado da nota de origem e rateado pela
                quantidade devolvida.
              </Text>
            </VStack>
          )}

          {etapa === 3 && (
            <VStack align="stretch" spacing={4}>
              <Box borderWidth="1px" borderRadius="md" p={3} bg="orange.50" borderColor="orange.200">
                <HStack justify="space-between" mb={2}>
                  <HStack spacing={2}>
                    <FileText size={15} color={ACCENT} />
                    <Text fontSize="sm" fontWeight="bold">
                      Anexar o PDF da {perfilAtivo?.autorizacao_label || "autorização"}
                    </Text>
                  </HStack>
                  {autorizacaoNome && (
                    <Tag size="sm" colorScheme="green" borderRadius="full">
                      <TagLabel>{autorizacaoNome}</TagLabel>
                    </Tag>
                  )}
                </HStack>
                <Input type="file" accept="application/pdf" size="sm" pt={1} bg="white"
                  isDisabled={lendoPdf}
                  onChange={(e) => e.target.files?.[0] && lerPdfAutorizacao(e.target.files[0])} />
                <Text fontSize="xs" color="gray.600" mt={2}>
                  {lendoPdf
                    ? "Lendo o PDF…"
                    : "Preenche os campos abaixo e segue anexado no e-mail ao fornecedor. Confira o que foi lido antes de criar."}
                </Text>
              </Box>

              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                <FormControl>
                  <FormLabel fontSize="sm">
                    {perfilAtivo?.autorizacao_label || "Nº da Autorização"}
                    {perfilAtivo?.exige_autorizacao && <Text as="span" color="red.500"> *</Text>}
                  </FormLabel>
                  <Input size="sm" value={form.autorizacao_numero}
                    onChange={(e) => setForm((f) => ({ ...f, autorizacao_numero: e.target.value }))} />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">Data da autorização</FormLabel>
                  <Input size="sm" type="date" value={form.autorizacao_data}
                    onChange={(e) => setForm((f) => ({ ...f, autorizacao_data: e.target.value }))} />
                </FormControl>

                {perfilAtivo?.motivos && Object.keys(perfilAtivo.motivos).length > 0 ? (
                  <FormControl>
                    <FormLabel fontSize="sm">Motivo</FormLabel>
                    <Select size="sm" value={form.motivo_codigo}
                      onChange={(e) => setForm((f) => ({
                        ...f, motivo_codigo: e.target.value,
                        motivo_texto: perfilAtivo.motivos[e.target.value] || "",
                      }))}>
                      <option value="">Selecione</option>
                      {Object.entries(perfilAtivo.motivos).map(([k, v]) =>
                        <option key={k} value={k}>{k} — {v}</option>)}
                    </Select>
                  </FormControl>
                ) : (
                  <FormControl>
                    <FormLabel fontSize="sm">Motivo</FormLabel>
                    <Input size="sm" value={form.motivo_texto}
                      onChange={(e) => setForm((f) => ({ ...f, motivo_texto: e.target.value }))} />
                  </FormControl>
                )}

                {perfilAtivo?.campos?.aos_cuidados && (
                  <FormControl>
                    <FormLabel fontSize="sm">A/C</FormLabel>
                    <Input size="sm" value={form.aos_cuidados}
                      onChange={(e) => setForm((f) => ({ ...f, aos_cuidados: e.target.value }))} />
                  </FormControl>
                )}
                <FormControl>
                  <FormLabel fontSize="sm">Vendedor</FormLabel>
                  <Input size="sm" value={form.vendedor}
                    onChange={(e) => setForm((f) => ({ ...f, vendedor: e.target.value }))} />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">Transportadora</FormLabel>
                  <Input size="sm" value={form.transportadora}
                    onChange={(e) => setForm((f) => ({ ...f, transportadora: e.target.value }))} />
                  {perfilAtivo?.transportadoras_proibidas?.length > 0 && (
                    <Text fontSize="xs" color="red.500" mt={1}>
                      Não aceita {perfilAtivo.transportadoras_proibidas.join(" / ")}.
                    </Text>
                  )}
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">Data de saída</FormLabel>
                  <Input size="sm" type="date" value={form.data_saida}
                    onChange={(e) => setForm((f) => ({ ...f, data_saida: e.target.value }))} />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">Previsão de entrega</FormLabel>
                  <Input size="sm" type="date" value={form.data_prevista_entrega}
                    onChange={(e) => setForm((f) => ({ ...f, data_prevista_entrega: e.target.value }))} />
                  {perfilAtivo?.dias_sem_recebimento?.includes(5) && (
                    <Text fontSize="xs" color="orange.600" mt={1}>
                      Este fornecedor não recebe devoluções às sextas-feiras.
                    </Text>
                  )}
                </FormControl>
              </SimpleGrid>

              <FormControl>
                <FormLabel fontSize="sm">Observação (vai para Dados Adicionais)</FormLabel>
                <Textarea size="sm" rows={2} value={form.observacao}
                  onChange={(e) => setForm((f) => ({ ...f, observacao: e.target.value }))} />
              </FormControl>

              <ListaEmails titulo="E-mails para envio do XML e do DANFE" valores={emails} onChange={setEmails} />
              <ListaEmails titulo="Em cópia" valores={emailsCopia} onChange={setEmailsCopia} />
            </VStack>
          )}
        </ModalBody>

        <ModalFooter gap={2}>
          {etapa > 1 && (
            <Button size="sm" variant="ghost" onClick={() => setEtapa((e) => e - 1)}>Voltar</Button>
          )}
          {etapa === 2 && (
            <Button size="sm" bg={ACCENT} color="white" _hover={{ bg: "#e07529" }}
              isDisabled={itensEscolhidos.length === 0} onClick={() => setEtapa(3)}>
              Continuar ({itensEscolhidos.length} {itensEscolhidos.length === 1 ? "item" : "itens"})
            </Button>
          )}
          {etapa === 3 && (
            <Button size="sm" bg={ACCENT} color="white" _hover={{ bg: "#e07529" }}
              isLoading={carregando} onClick={criar}>
              Criar rascunho
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

function ListaEmails({ titulo, valores, onChange }) {
  const [novo, setNovo] = useState("");

  // Vírgula e ponto e vírgula separam endereços: colar "a@x.com," vira o endereço
  // limpo, e não um e-mail com vírgula que depois bloqueia a emissão.
  const adicionar = () => {
    const novos = novo.split(/[,;]+/).map((e) => e.trim())
      .filter((e) => e && !valores.includes(e));
    if (novos.length === 0) { setNovo(""); return; }
    onChange([...valores, ...new Set(novos)]);
    setNovo("");
  };

  return (
    <FormControl>
      <FormLabel fontSize="sm">{titulo}</FormLabel>
      <HStack mb={2}>
        <Input size="sm" type="email" placeholder="email@fornecedor.com.br" value={novo}
          onChange={(e) => setNovo(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); adicionar(); } }} />
        <Button size="sm" onClick={adicionar}>Adicionar</Button>
      </HStack>
      <Wrap>
        {valores.map((email) => (
          <WrapItem key={email}>
            <Tag size="sm" colorScheme="orange" borderRadius="full">
              <TagLabel>{email}</TagLabel>
              <TagCloseButton onClick={() => onChange(valores.filter((v) => v !== email))} />
            </Tag>
          </WrapItem>
        ))}
      </Wrap>
    </FormControl>
  );
}

/* ------------------------------------------------------------------------- */
/* Detalhe — prévia fiscal, autorização, validação e emissão                   */
/* ------------------------------------------------------------------------- */

function DrawerDetalhe({ id, perfis, homologacaoDisponivel, onFechar, toast }) {
  const [d, setD] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [acao, setAcao] = useState(null);
  const [justificativa, setJustificativa] = useState("");
  // Homologação (tpAmb=2) é ensaio: sai a nota na SEFAZ de teste, sem valor fiscal.
  const [ambiente, setAmbiente] = useState("producao");
  // Extração do PDF erra: o operador precisa poder corrigir antes de emitir.
  const [editando, setEditando] = useState(false);
  const [rascunho, setRascunho] = useState({});
  // Lista de destinatários também se corrige no rascunho: um endereço torto bloqueia
  // a emissão e antes não havia por onde arrumar.
  const [editandoEmails, setEditandoEmails] = useState(false);
  const [emailsRascunho, setEmailsRascunho] = useState([]);
  const [emailsCopiaRascunho, setEmailsCopiaRascunho] = useState([]);

  const carregar = useCallback(async () => {
    const res = await getDevolucaoFornecedor(id);
    if (!res?.error) setD(res);
    setCarregando(false);
  }, [id]);

  useEffect(() => { carregar(); }, [carregar]);

  const perfilAtivo = useMemo(
    () => perfis.find((p) => p.chave === d?.perfil) || null, [perfis, d]
  );

  const executar = async (nome, fn, sucesso) => {
    setAcao(nome);
    const res = await fn();
    setAcao(null);

    if (res?.error) {
      toast({
        title: "Não deu certo", description: res.error, status: "error",
        duration: 9000, isClosable: true,
      });
      if (res.validacao) setD((old) => ({ ...old, validacao: res.validacao }));
      return;
    }
    if (sucesso) toast({ title: sucesso, status: "success", duration: 5000 });
    await carregar();
  };

  const abrirEdicao = () => {
    setRascunho({
      autorizacao_numero: d.autorizacao_numero || "",
      autorizacao_data: d.autorizacao_data ? String(d.autorizacao_data).slice(0, 10) : "",
      motivo_codigo: d.motivo_codigo || "",
      motivo_texto: d.motivo_texto || "",
      aos_cuidados: d.aos_cuidados || "",
      vendedor: d.vendedor || "",
      transportadora: d.transportadora || "",
      data_saida: d.data_saida ? String(d.data_saida).slice(0, 10) : "",
      data_prevista_entrega: d.data_prevista_entrega ? String(d.data_prevista_entrega).slice(0, 10) : "",
      observacao: d.observacao || "",
    });
    setEditando(true);
  };

  const salvarEdicao = async () => {
    setAcao("salvar");
    const res = await atualizarDevolucaoFornecedor(d.id, rascunho);
    setAcao(null);
    if (res?.error) {
      toast({ title: "Não foi possível salvar", description: res.error, status: "error", duration: 7000 });
      return;
    }
    setEditando(false);
    toast({ title: "Dados atualizados", status: "success", duration: 3000 });
    await carregar();
  };

  const abrirEdicaoEmails = () => {
    setEmailsRascunho(d.emails || []);
    setEmailsCopiaRascunho(d.emails_copia || []);
    setEditandoEmails(true);
  };

  const salvarEmails = async () => {
    setAcao("emails");
    const res = await atualizarDevolucaoFornecedor(d.id, {
      emails: emailsRascunho, emails_copia: emailsCopiaRascunho,
    });
    setAcao(null);
    if (res?.error) {
      toast({ title: "Não foi possível salvar os e-mails", description: res.error, status: "error", duration: 7000 });
      return;
    }
    setEditandoEmails(false);
    toast({ title: "Lista de envio atualizada", status: "success", duration: 3000 });
    await carregar();
  };

  const enviarPdf = async (arquivo) => {
    setAcao("autorizacao");
    const res = await enviarAutorizacaoDevolucaoFornecedor(id, arquivo);
    setAcao(null);
    if (res?.error) {
      toast({ title: "Falha ao enviar a autorização", description: res.error, status: "error", duration: 7000 });
      return;
    }
    const origem = res.extracao?.origem;
    toast({
      title: "Autorização anexada",
      description: origem === "ocr" ? "Campos lidos por OCR — confira antes de emitir."
        : origem === "indisponivel" ? "Leitura automática indisponível: preencha os campos à mão."
        : "Campos extraídos do PDF.",
      status: origem === "texto" ? "success" : "warning", duration: 7000,
    });
    setD(res.devolucao);
  };

  if (carregando || !d) {
    return (
      <Drawer isOpen placement="right" size="xl" onClose={onFechar}>
        <DrawerOverlay />
        <DrawerContent><Center h="100%"><Spinner color={ACCENT} /></Center></DrawerContent>
      </Drawer>
    );
  }

  const editavel = ["rascunho", "validado"].includes(d.status);
  const emitivel = editavel;
  const val = d.validacao;

  return (
    <Drawer isOpen placement="right" size="xl" onClose={onFechar}>
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader borderBottomWidth="1px">
          <HStack spacing={3}>
            <Text fontSize="md">Devolução #{d.id}</Text>
            {selo(d.status)}
            <Text fontSize="xs" color="gray.500" textTransform="uppercase">{d.perfil}</Text>
          </HStack>
          <Text fontSize="xs" color="gray.500" fontWeight="normal" mt={1}>
            NF de origem {d.nfe_origem_numero || "—"} · {formatDate(d.nfe_origem_emissao)}
            {d.nfe_numero ? ` · NF-e de devolução ${d.nfe_numero}` : ""}
          </Text>
        </DrawerHeader>

        <DrawerBody>
          <VStack align="stretch" spacing={5} pb={8}>
            {d.erro && (
              <Alert status="error" fontSize="xs" borderRadius="md">
                <AlertIcon /><Text>{d.erro}</Text>
              </Alert>
            )}

            {/* Validação */}
            {val && (
              <Box>
                {val.bloqueios?.length > 0 && (
                  <Alert status="error" borderRadius="md" alignItems="flex-start" mb={2}>
                    <AlertIcon />
                    <Box fontSize="xs">
                      <Text fontWeight="bold" mb={1}>
                        {val.bloqueios.length} {val.bloqueios.length === 1 ? "bloqueio" : "bloqueios"} — o fornecedor recusaria
                      </Text>
                      {val.bloqueios.map((b, i) => <Text key={i}>• {b}</Text>)}
                    </Box>
                  </Alert>
                )}
                {val.avisos?.length > 0 && (
                  <Alert status="warning" borderRadius="md" alignItems="flex-start">
                    <AlertIcon />
                    <Box fontSize="xs">
                      <Text fontWeight="bold" mb={1}>Avisos</Text>
                      {val.avisos.map((a, i) => <Text key={i}>• {a}</Text>)}
                    </Box>
                  </Alert>
                )}
                {val.ok && val.avisos?.length === 0 && (
                  <Alert status="success" borderRadius="md" fontSize="xs">
                    <AlertIcon />Passou em todas as regras deste fornecedor.
                  </Alert>
                )}
              </Box>
            )}

            {/* Autorização */}
            <Box borderWidth="1px" borderRadius="md" p={3}>
              <HStack justify="space-between" mb={2}>
                <HStack><ShieldCheck size={16} color={ACCENT} />
                  <Text fontSize="sm" fontWeight="bold">
                    {perfilAtivo?.autorizacao_label || "Autorização"}
                  </Text>
                </HStack>
                <HStack spacing={2}>
                  {d.autorizacao_pdf_path && (
                    <Button as="a" href={`/${d.autorizacao_pdf_path}`} target="_blank" rel="noreferrer"
                      size="xs" variant="outline" leftIcon={<FileText size={13} />}>Ver PDF</Button>
                  )}
                  {editavel && !editando && (
                    <Button size="xs" variant="outline" leftIcon={<Pencil size={13} />}
                      onClick={abrirEdicao}>Editar</Button>
                  )}
                </HStack>
              </HStack>
              {editando ? (
                <VStack align="stretch" spacing={3}>
                  <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
                    <CampoEdit rotulo={perfilAtivo?.autorizacao_label || "Número"} campo="autorizacao_numero"
                      valores={rascunho} onChange={setRascunho} />
                    <CampoEdit rotulo="Data da autorização" campo="autorizacao_data" tipo="date"
                      valores={rascunho} onChange={setRascunho} />
                    <FormControl>
                      <FormLabel fontSize="xs" mb={1}>Motivo</FormLabel>
                      {perfilAtivo?.motivos && Object.keys(perfilAtivo.motivos).length > 0 ? (
                        <Select size="sm" value={rascunho.motivo_codigo || ""}
                          onChange={(e) => setRascunho((r) => ({
                            ...r, motivo_codigo: e.target.value,
                            motivo_texto: perfilAtivo.motivos[e.target.value] || r.motivo_texto,
                          }))}>
                          <option value="">Selecione</option>
                          {Object.entries(perfilAtivo.motivos).map(([k, v]) =>
                            <option key={k} value={k}>{k} — {v}</option>)}
                        </Select>
                      ) : (
                        <Input size="sm" value={rascunho.motivo_texto || ""}
                          onChange={(e) => setRascunho((r) => ({ ...r, motivo_texto: e.target.value }))} />
                      )}
                    </FormControl>
                    <CampoEdit rotulo="A/C" campo="aos_cuidados" valores={rascunho} onChange={setRascunho} />
                    <CampoEdit rotulo="Vendedor" campo="vendedor" valores={rascunho} onChange={setRascunho} />
                    <CampoEdit rotulo="Transportadora" campo="transportadora" valores={rascunho} onChange={setRascunho} />
                    <CampoEdit rotulo="Data de saída" campo="data_saida" tipo="date" valores={rascunho} onChange={setRascunho} />
                    <CampoEdit rotulo="Previsão de entrega" campo="data_prevista_entrega" tipo="date" valores={rascunho} onChange={setRascunho} />
                  </SimpleGrid>
                  <FormControl>
                    <FormLabel fontSize="xs" mb={1}>Observação</FormLabel>
                    <Textarea size="sm" rows={2} value={rascunho.observacao || ""}
                      onChange={(e) => setRascunho((r) => ({ ...r, observacao: e.target.value }))} />
                  </FormControl>
                  <HStack>
                    <Button size="sm" bg={ACCENT} color="white" _hover={{ bg: "#e07529" }}
                      isLoading={acao === "salvar"} onClick={salvarEdicao}>Salvar</Button>
                    <Button size="sm" variant="ghost" onClick={() => setEditando(false)}>Cancelar</Button>
                  </HStack>
                  <Text fontSize="10px" color="gray.500">
                    Salvar recalcula os Dados Adicionais e devolve o pedido para rascunho — valide de novo antes de emitir.
                  </Text>
                </VStack>
              ) : (
              <SimpleGrid columns={{ base: 2, md: 3 }} spacing={2} fontSize="xs">
                <Campo rotulo="Número" valor={d.autorizacao_numero} />
                <Campo rotulo="Data" valor={formatDate(d.autorizacao_data)} />
                <Campo rotulo="Válida até" valor={formatDate(d.autorizacao_validade)} />
                <Campo rotulo="Motivo" valor={[d.motivo_codigo, d.motivo_texto].filter(Boolean).join(" — ")} />
                <Campo rotulo="A/C" valor={d.aos_cuidados} />
                <Campo rotulo="Vendedor" valor={d.vendedor} />
                <Campo rotulo="Transportadora" valor={d.transportadora} />
                <Campo rotulo="Saída" valor={formatDate(d.data_saida)} />
                <Campo rotulo="Previsão de entrega" valor={formatDate(d.data_prevista_entrega)} />
              </SimpleGrid>
              )}

              {editavel && (
                <Box mt={3}>
                  <FormLabel fontSize="xs" mb={1}>Anexar o PDF da autorização (extrai os campos)</FormLabel>
                  <Input type="file" accept="application/pdf" size="sm" pt={1}
                    isDisabled={acao === "autorizacao"}
                    onChange={(e) => e.target.files?.[0] && enviarPdf(e.target.files[0])} />
                </Box>
              )}
            </Box>

            {/* Prévia fiscal */}
            <Box>
              <Text fontSize="sm" fontWeight="bold" mb={2}>Prévia fiscal</Text>
              <Box overflowX="auto" borderWidth="1px" borderRadius="md">
                <Table size="sm">
                  <Thead bg="gray.50">
                    <Tr>
                      <Th>Item</Th><Th>Cen.</Th><Th>CFOP</Th><Th>CSOSN</Th>
                      <Th isNumeric>Qtd</Th><Th isNumeric>Total</Th>
                      <Th isNumeric>BC ICMS</Th><Th isNumeric>ICMS</Th>
                      <Th isNumeric>ST ret.</Th><Th isNumeric>Outras desp.</Th>
                      {editavel && <Th></Th>}
                    </Tr>
                  </Thead>
                  <Tbody>
                    {(d.itens || []).map((i) => (
                      <Tr key={i.id}>
                        <Td>
                          <Text fontSize="xs" fontWeight="bold">{i.codigo}</Text>
                          <Text fontSize="10px" color="gray.500" noOfLines={1}>{i.descricao}</Text>
                        </Td>
                        <Td>
                          <Tooltip label={CENARIOS[i.cenario]?.ajuda} fontSize="xs">
                            <Badge colorScheme={CENARIOS[i.cenario]?.cor} fontSize="10px">{i.cenario}</Badge>
                          </Tooltip>
                        </Td>
                        <Td fontSize="xs">
                          <Text color="gray.400" as="span">{i.cfop_origem} → </Text>
                          <Text as="span" fontWeight="bold">{i.cfop}</Text>
                        </Td>
                        <Td fontSize="xs">{i.csosn}</Td>
                        <Td isNumeric fontSize="xs">{i.quantidade} / {i.quantidade_origem}</Td>
                        <Td isNumeric fontSize="xs">{formatBRL(i.valor_total)}</Td>
                        <Td isNumeric fontSize="xs">{formatBRL(i.vbc)}</Td>
                        <Td isNumeric fontSize="xs">{formatBRL(i.valor_icms)}</Td>
                        <Td isNumeric fontSize="xs">{formatBRL(i.valor_st_ret)}</Td>
                        <Td isNumeric fontSize="xs">{formatBRL(i.valor_outros)}</Td>
                        {editavel && (
                          <Td>
                            {(d.itens || []).length > 1 && (
                              <Tooltip label="Remover item">
                                <IconButton size="xs" variant="ghost" colorScheme="red"
                                  aria-label="Remover" icon={<Trash2 size={13} />}
                                  onClick={() => executar("remover",
                                    () => removerItemDevolucaoFornecedor(d.id, i.id), "Item removido")} />
                              </Tooltip>
                            )}
                          </Td>
                        )}
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>

              <SimpleGrid columns={{ base: 2, md: 4 }} spacing={2} mt={3} fontSize="xs">
                <Total rotulo="Produtos" valor={d.totais?.vProd} />
                <Total rotulo="Base ICMS" valor={d.totais?.vBC} />
                <Total rotulo="ICMS" valor={d.totais?.vICMS} />
                <Total rotulo="ST retida" valor={d.totais?.vICMSSTRet} />
                <Total rotulo="ICMS-ST (em vOutro)" valor={d.totais?.vST} />
                <Total rotulo="IPI (em vOutro)" valor={d.totais?.vIPI} />
                <Total rotulo="Outras despesas" valor={d.totais?.vOutro} />
                <Total rotulo="Total da nota" valor={d.totais?.vNF} destaque />
              </SimpleGrid>
              <Text fontSize="11px" color="gray.500" mt={2}>
                ICMS-ST e IPI não vão em campo próprio: entram somados em Outras Despesas
                Acessórias e detalhados nos Dados Adicionais.
              </Text>
            </Box>

            {/* Dados adicionais */}
            {d.inf_cpl && (
              <Box>
                <Text fontSize="sm" fontWeight="bold" mb={1}>Dados Adicionais (infCpl)</Text>
                <Box borderWidth="1px" borderRadius="md" p={3} bg="gray.50">
                  <Text fontSize="xs" color="gray.700">{d.inf_cpl}</Text>
                </Box>
              </Box>
            )}

            {/* E-mails */}
            <Box>
              <HStack justify="space-between" mb={1}>
                <Text fontSize="sm" fontWeight="bold">Envio</Text>
                {editavel && !editandoEmails && (
                  <Button size="xs" variant="outline" leftIcon={<Pencil size={13} />}
                    onClick={abrirEdicaoEmails}>Editar</Button>
                )}
              </HStack>

              {editandoEmails ? (
                <VStack align="stretch" spacing={3}>
                  <ListaEmails titulo="E-mails para envio do XML e do DANFE"
                    valores={emailsRascunho} onChange={setEmailsRascunho} />
                  <ListaEmails titulo="Em cópia"
                    valores={emailsCopiaRascunho} onChange={setEmailsCopiaRascunho} />
                  <HStack>
                    <Button size="sm" bg={ACCENT} color="white" _hover={{ bg: "#e07529" }}
                      isLoading={acao === "emails"} onClick={salvarEmails}>Salvar</Button>
                    <Button size="sm" variant="ghost" onClick={() => setEditandoEmails(false)}>Cancelar</Button>
                  </HStack>
                  <Text fontSize="10px" color="gray.500">
                    Vírgula e ponto e vírgula separam endereços — não fazem parte deles.
                    Salvar devolve o pedido para rascunho: valide de novo antes de emitir.
                  </Text>
                </VStack>
              ) : (
                <Wrap>
                  {(d.emails || []).map((e) => (
                    <WrapItem key={e}>
                      <Tag size="sm" colorScheme={emailValido(e) ? "orange" : "red"}>
                        <TagLabel>{e}</TagLabel>
                      </Tag>
                    </WrapItem>
                  ))}
                  {(d.emails_copia || []).map((e) => (
                    <WrapItem key={e}>
                      <Tag size="sm" variant={emailValido(e) ? "outline" : "solid"}
                        colorScheme={emailValido(e) ? "gray" : "red"}>
                        <TagLabel>cc: {e}</TagLabel>
                      </Tag>
                    </WrapItem>
                  ))}
                  {(d.emails || []).length === 0 && (d.emails_copia || []).length === 0 && (
                    <WrapItem><Text fontSize="xs" color="gray.500">Nenhum destinatário.</Text></WrapItem>
                  )}
                </Wrap>
              )}
            </Box>

            {/* Último teste em homologação */}
            {d.teste_resultado && (
              <Box borderWidth="1px" borderColor="yellow.300" borderRadius="md" p={3} bg="yellow.50">
                <HStack justify="space-between" mb={2}>
                  <HStack>
                    <FlaskConical size={16} color="#B7791F" />
                    <Text fontSize="sm" fontWeight="bold">Último teste em homologação</Text>
                  </HStack>
                  <Text fontSize="11px" color="gray.600">{formatDateTime(d.teste_em)}</Text>
                </HStack>

                {d.teste_resultado.erro ? (
                  <Alert status="error" fontSize="xs" borderRadius="md">
                    <AlertIcon /><Text>{d.teste_resultado.erro}</Text>
                  </Alert>
                ) : (
                  <>
                    <SimpleGrid columns={{ base: 2, md: 4 }} spacing={2} fontSize="xs">
                      <Campo rotulo="Retorno" valor={d.teste_resultado.status} />
                      <Campo rotulo="Número" valor={d.teste_resultado.numero} />
                      <Campo rotulo="Série" valor={d.teste_resultado.serie} />
                      <Campo rotulo="Protocolo" valor={d.teste_resultado.protocolo} />
                    </SimpleGrid>
                    {d.teste_resultado.chave && (
                      <Text fontSize="10px" color="gray.600" mt={1} wordBreak="break-all">
                        Chave {d.teste_resultado.chave}
                      </Text>
                    )}
                    <HStack mt={2} spacing={2}>
                      {d.teste_resultado.xml_disponivel && (
                        <Button size="xs" variant="outline" leftIcon={<Download size={13} />}
                          onClick={() => baixarArquivoDevolucaoFornecedor(d.id, "xml", true)}>
                          XML do teste
                        </Button>
                      )}
                      {d.teste_resultado.nfe_id && (
                        <Button size="xs" variant="outline" leftIcon={<FileText size={13} />}
                          onClick={() => baixarArquivoDevolucaoFornecedor(d.id, "danfe", true)}>
                          DANFE do teste
                        </Button>
                      )}
                    </HStack>
                  </>
                )}

                <Text fontSize="10px" color="gray.600" mt={2}>
                  Nota de teste não tem valor fiscal: o estoque não foi baixado, o fornecedor
                  não recebeu e-mail e o pedido continua podendo ser emitido em produção.
                </Text>
              </Box>
            )}

            {/* Ações */}
            <Divider />
            <Wrap spacing={2}>
              {emitivel && (
                <>
                  <WrapItem>
                    <Button size="sm" variant="outline" leftIcon={<CheckCircle size={15} />}
                      isLoading={acao === "validar"}
                      onClick={() => executar("validar", () => validarDevolucaoFornecedor(d.id))}>
                      Validar
                    </Button>
                  </WrapItem>
                  <WrapItem>
                    <Tooltip isDisabled={homologacaoDisponivel}
                      label="Emissão em homologação desligada no servidor: sem o faturador tratando o campo ambiente, o teste sairia em produção, valendo de verdade.">
                      <Select size="sm" w="200px" value={ambiente}
                        borderColor={ambiente === "homologacao" ? "yellow.400" : undefined}
                        onChange={(e) => setAmbiente(e.target.value)}>
                        <option value="producao">Produção — vale</option>
                        <option value="homologacao" disabled={!homologacaoDisponivel}>
                          Homologação — teste{homologacaoDisponivel ? "" : " (indisponível)"}
                        </option>
                      </Select>
                    </Tooltip>
                  </WrapItem>
                  <WrapItem>
                    {ambiente === "homologacao" ? (
                      <Button size="sm" colorScheme="yellow" variant="solid"
                        leftIcon={<FlaskConical size={15} />} isLoading={acao === "emitir"}
                        onClick={() => executar("emitir",
                          () => emitirDevolucaoFornecedor(d.id, "homologacao"),
                          "NF-e de teste emitida em homologação")}>
                        Emitir teste
                      </Button>
                    ) : (
                      <Button size="sm" bg={ACCENT} color="white" _hover={{ bg: "#e07529" }}
                        leftIcon={<Send size={15} />} isLoading={acao === "emitir"}
                        onClick={() => executar("emitir",
                          () => emitirDevolucaoFornecedor(d.id, "producao"),
                          "NF-e de devolução emitida")}>
                        Emitir NF-e
                      </Button>
                    )}
                  </WrapItem>
                </>
              )}

              {d.nfe_xml && (
                <WrapItem>
                  <Button size="sm" variant="outline" leftIcon={<Download size={15} />}
                    onClick={() => baixarArquivoDevolucaoFornecedor(d.id, "xml")}>XML</Button>
                </WrapItem>
              )}
              {d.nfe_id && (
                <WrapItem>
                  <Button size="sm" variant="outline" leftIcon={<FileText size={15} />}
                    onClick={() => baixarArquivoDevolucaoFornecedor(d.id, "danfe")}>DANFE</Button>
                </WrapItem>
              )}

              {["autorizado", "enviado"].includes(d.status) && (
                <WrapItem>
                  <Button size="sm" variant="outline" colorScheme="purple" leftIcon={<Truck size={15} />}
                    isLoading={acao === "despachado"}
                    onClick={() => executar("despachado",
                      () => alterarStatusDevolucaoFornecedor(d.id, { status: "despachado" }),
                      "Marcada como despachada")}>
                    Despachado
                  </Button>
                </WrapItem>
              )}
              {["despachado", "enviado"].includes(d.status) && (
                <>
                  <WrapItem>
                    <Button size="sm" variant="outline" colorScheme="green" leftIcon={<CheckCircle size={15} />}
                      isLoading={acao === "confirmado"}
                      onClick={() => executar("confirmado",
                        () => alterarStatusDevolucaoFornecedor(d.id, { status: "confirmado" }),
                        "Devolução confirmada pelo fornecedor")}>
                      Confirmado
                    </Button>
                  </WrapItem>
                  <WrapItem>
                    <Button size="sm" variant="outline" colorScheme="red" leftIcon={<AlertTriangle size={15} />}
                      isLoading={acao === "recusado"}
                      onClick={() => executar("recusado",
                        () => alterarStatusDevolucaoFornecedor(d.id, { status: "recusado" }),
                        "Devolução marcada como recusada")}>
                      Recusado
                    </Button>
                  </WrapItem>
                </>
              )}
            </Wrap>

            {/* Cancelamento */}
            {["autorizado", "enviado", "despachado"].includes(d.status) && (
              <Box borderWidth="1px" borderColor="red.200" borderRadius="md" p={3}>
                <HStack mb={2}><XCircle size={15} color="#E53E3E" />
                  <Text fontSize="sm" fontWeight="bold" color="red.600">Cancelar a NF-e</Text>
                </HStack>
                <Text fontSize="xs" color="gray.600" mb={2}>
                  Nota autorizada não se corrige: cancela e refaz. As peças voltam ao estoque.
                </Text>
                <Textarea size="sm" rows={2} placeholder="Justificativa (15 a 255 caracteres)"
                  value={justificativa} onChange={(e) => setJustificativa(e.target.value)} />
                <Button mt={2} size="sm" colorScheme="red" variant="outline"
                  isDisabled={justificativa.trim().length < 15} isLoading={acao === "cancelar"}
                  onClick={() => executar("cancelar",
                    () => cancelarDevolucaoFornecedor(d.id, justificativa), "NF-e cancelada")}>
                  Cancelar NF-e
                </Button>
              </Box>
            )}

            <Text fontSize="10px" color="gray.400">
              Criada em {formatDateTime(d.created_at)}
              {d.emitida_em ? ` · emitida em ${formatDateTime(d.emitida_em)}` : ""}
              {d.estoque_baixado ? " · estoque baixado" : ""}
            </Text>
          </VStack>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
}

const CampoEdit = ({ rotulo, campo, tipo = "text", valores, onChange }) => (
  <FormControl>
    <FormLabel fontSize="xs" mb={1}>{rotulo}</FormLabel>
    <Input size="sm" type={tipo} value={valores[campo] || ""}
      onChange={(e) => onChange((r) => ({ ...r, [campo]: e.target.value }))} />
  </FormControl>
);

const Campo = ({ rotulo, valor }) => (
  <Box>
    <Text color="gray.500" fontSize="10px" textTransform="uppercase">{rotulo}</Text>
    <Text fontWeight="medium">{valor || "—"}</Text>
  </Box>
);

const Total = ({ rotulo, valor, destaque }) => (
  <Box borderWidth="1px" borderRadius="md" p={2} bg={destaque ? "orange.50" : "white"}
    borderColor={destaque ? "orange.200" : "gray.200"}>
    <Text color="gray.500" fontSize="10px" textTransform="uppercase">{rotulo}</Text>
    <Text fontWeight="bold" fontSize="sm">{formatBRL(valor || 0)}</Text>
  </Box>
);
